import {
	IconExternalLink,
	IconGitBranch,
	IconGitCommit,
	IconTrash,
} from "@tabler/icons-react";
import {
	Button,
	Checkbox,
	Flex,
	Space,
	Switch,
	Tag,
	Tooltip,
	Typography,
} from "antd";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState } from "react";
import { useLinkProjectRepository } from "@/common/artemis-operations/project-repositories";
import {
	useGitProviderConnectionRepositories,
	useGitProviderRepositoryBranches,
	useGitProviderRepositoryCommits,
} from "@/common/artemis-operations/workspaces";
import type { GitProviderRepository, ProjectRepository } from "@/common/model";
import { useNotify } from "@/common/use-notify";
import {
	BaseContainer,
	CustomIcon,
	IdField,
	QueryGuard,
} from "@/components/base";
import { BaseDetails } from "@/components/base-details";
import { CardBody, CardViewer } from "@/components/card-viewer";
import { CustomList } from "@/components/custom-list";
import { CustomDrawer } from "@/components/layout/workspace/custom-drawer";

interface ProjectRepositoryBrowserProps {
	open: boolean;
	onClose: () => void;
	workspaceId: string;
	projectId: string;
	gitProviderConnectionId: string;
	/** Called after at least one repository was successfully linked, so the caller can refetch. */
	onLinked: () => void;
}

/**
 * Browse the workspace's connected GitHub installation and link one or more repositories to a
 * project. Directly follows `TemplateGalleryBrowser`'s toolbar/multi-select shape, but the list
 * comes from `usePagedList` driven by the (non-relay) `gitProviderConnectionRepositories` query.
 */
export function ProjectRepositoryBrowser({
	open,
	onClose,
	workspaceId,
	projectId,
	gitProviderConnectionId,
	onLinked,
}: ProjectRepositoryBrowserProps) {
	const notify = useNotify();
	const list = useGitProviderConnectionRepositories(workspaceId);
	const linkRepository = useLinkProjectRepository();

	const [multiMode, setMultiMode] = useState(false);
	const [selected, setSelected] = useState<Map<string, GitProviderRepository>>(
		new Map(),
	);
	const [singleSelected, setSingleSelected] = useState<GitProviderRepository>();
	const [saving, setSaving] = useState(false);

	// Reset local selection state on close; state declared here would otherwise persist
	// into the next open (the drawer stays mounted between opens).
	useEffect(() => {
		if (open) return;
		setMultiMode(false);
		setSelected(new Map());
		setSingleSelected(undefined);
		setSaving(false);
	}, [open]);

	const toggleSelected = (r: GitProviderRepository) => {
		setSelected((prev) => {
			const next = new Map(prev);
			if (next.has(r.id)) next.delete(r.id);
			else next.set(r.id, r);
			return next;
		});
	};

	const selectedRepos = multiMode
		? Array.from(selected.values())
		: singleSelected
			? [singleSelected]
			: [];

	const linkOne = (repository: GitProviderRepository) =>
		new Promise<boolean>((resolve) => {
			linkRepository.mutate(
				{
					projectId,
					gitProviderConnectionId,
					repositoryExternalId: repository.id,
					repositoryFullName: repository.fullName,
				},
				{ onSuccess: () => resolve(true), onError: () => resolve(false) },
			);
		});

	const handleConfirm = async () => {
		if (selectedRepos.length === 0) return;
		setSaving(true);
		try {
			// Sequential, not Promise.all: linkRepository shares one useMutation instance, which
			// tracks only the most recent call's onSuccess/onError — firing several mutate() calls
			// concurrently drops every callback but the last (see TemplateGalleryBrowser's createOne).
			const results: boolean[] = [];
			for (const repository of selectedRepos) {
				results.push(await linkOne(repository));
			}
			const succeeded = results.filter(Boolean).length;
			if (succeeded > 0) {
				notify.success(
					`${succeeded} repositor${succeeded === 1 ? "y" : "ies"} linked`,
				);
				onLinked();
			}
			if (succeeded < results.length) {
				notify.error("Some repositories could not be linked");
			}
			onClose();
		} finally {
			setSaving(false);
		}
	};

	return (
		<CustomDrawer
			title="Link a repository"
			open={open}
			onClose={onClose}
			size="large"
			footer={
				<Space style={{ display: "flex", justifyContent: "flex-end" }}>
					<Button onClick={onClose}>Cancel</Button>
					<Button
						type="primary"
						loading={saving}
						disabled={selectedRepos.length === 0}
						onClick={handleConfirm}
					>
						Link
						{selectedRepos.length > 0 ? ` ${selectedRepos.length}` : ""}
					</Button>
				</Space>
			}
		>
			<QueryGuard isPending={list.loading} isError={list.isError}>
				<CardViewer
					items={list.rows}
					getKey={(r) => r.id}
					onOpen={(r) => (multiMode ? toggleSelected(r) : setSingleSelected(r))}
					isSelected={(r) =>
						multiMode ? selected.has(r.id) : singleSelected?.id === r.id
					}
					searchText={(r) => r.fullName}
					searchPlaceholder="Search repositories"
					minCardWidth={280}
					server={{
						search: list.search,
						onSearchChange: list.setSearch,
						filterValues: {},
						onFilterChange: () => {},
						onSortChange: () => {},
						totalCount: list.totalCount,
						hasNextPage: list.hasNextPage,
						hasPreviousPage: list.hasPreviousPage,
						onNextPage: list.onNextPage,
						onPreviousPage: list.onPreviousPage,
						pageSize: list.pageSize,
						pageIndex: list.pageIndex,
					}}
					extra={
						<Space>
							<Typography.Text>Select multiple</Typography.Text>
							<Switch
								size="small"
								checked={multiMode}
								onChange={(checked) => {
									setMultiMode(checked);
									setSelected(new Map());
									setSingleSelected(undefined);
								}}
							/>
						</Space>
					}
					renderCard={(r) => (
						<CardBody
							title={r.fullName}
							leading={
								multiMode && (
									<Checkbox
										checked={selected.has(r.id)}
										onClick={(e) => {
											e.stopPropagation();
											toggleSelected(r);
										}}
									/>
								)
							}
							// Hardcoded for now — the only connected provider is GitHub
							// (GitProviderType has one member). Swap for a real field on
							// GitProviderRepository once a second provider ships.
							description={`GitHub · ${r.ownerType} · ${r.private ? "Private" : "Public"}`}
							extra={r.private ? <Tag>Private</Tag> : <Tag>Public</Tag>}
						/>
					)}
				/>
			</QueryGuard>
		</CustomDrawer>
	);
}

/** Drawer showing a linked repository's details, with the ability to unlink it. */
export function ProjectRepositoryViewerDrawer({
	repository,
	open,
	onClose,
	onUnlink,
}: {
	repository: ProjectRepository | null;
	open: boolean;
	onClose: () => void;
	onUnlink: () => void;
}) {
	const commits = useGitProviderRepositoryCommits(repository?.id ?? "");
	const branches = useGitProviderRepositoryBranches(repository?.id ?? "");

	if (!repository) return null;
	return (
		<CustomDrawer
			title={repository.repositoryFullName}
			open={open}
			onClose={onClose}
			extra={
				<Space>
					<Tooltip title="Open in GitHub">
						<Button
							type="text"
							icon={<CustomIcon icon={IconExternalLink} />}
							onClick={() =>
								window.open(
									`https://github.com/${repository.repositoryFullName}`,
									"_blank",
								)
							}
						/>
					</Tooltip>
					<Tooltip title="Unlink repository">
						<Button
							danger
							type="text"
							icon={<CustomIcon icon={IconTrash} />}
							onClick={onUnlink}
						/>
					</Tooltip>
				</Space>
			}
		>
			<Space vertical style={{ width: "100%" }} size="large">
				<BaseDetails
					item={repository}
					fields={[
						{
							key: "id",
							label: "Repository ID",
							render: (id) => <IdField id={id} showCopyButton />,
						},
						{ key: "repositoryFullName", label: "Repository" },
						{ key: "defaultBranch", label: "Default branch" },
						{ key: "provider", label: "Provider" },
					]}
				/>

				<BaseContainer
					size="small"
					title="Recent commits"
					actions={
						<Typography.Link
							href={`https://github.com/${repository.repositoryFullName}/commits`}
							target="_blank"
						>
							View all
						</Typography.Link>
					}
				>
					<QueryGuard isPending={commits.loading} isError={commits.isError}>
						<CustomList
							data={commits.rows}
							onItemClick={(c) => window.open(c.htmlUrl, "_blank noopener")}
							renderLeading={() => <CustomIcon icon={IconGitCommit} />}
							// Commits come newest first, so the first row of the first page is HEAD.
							renderTrailing={(_, index) =>
								commits.pageIndex === 0 && index === 0 && <Tag>HEAD</Tag>
							}
							renderItem={(c) => (
								<Flex vertical style={{ minWidth: 0 }}>
									<Typography.Text ellipsis>{c.message}</Typography.Text>
									<Typography.Text
										type="secondary"
										style={{ fontSize: 12 }}
										ellipsis={{ tooltip: true }}
									>
										{[
											c.sha.slice(0, 7),
											c.authorName ?? "unknown",
											c.authoredAt
												? formatDistanceToNow(
														new Date(c.authoredAt as string),
														{
															addSuffix: true,
														},
													)
												: null,
										]
											.filter(Boolean)
											.join(" · ")}
									</Typography.Text>
								</Flex>
							)}
						/>
					</QueryGuard>
				</BaseContainer>

				<BaseContainer
					size="small"
					title="Branches"
					actions={
						<Typography.Link
							href={`https://github.com/${repository.repositoryFullName}/branches`}
							target="_blank"
						>
							View all
						</Typography.Link>
					}
				>
					<QueryGuard isPending={branches.loading} isError={branches.isError}>
						<CustomList
							data={branches.rows}
							onItemClick={(b) =>
								window.open(
									`https://github.com/${repository.repositoryFullName}/tree/${b.name}`,
									"_blank",
								)
							}
							renderLeading={() => <CustomIcon icon={IconGitBranch} />}
							renderTrailing={(b) => b.isDefault && <Tag>Default</Tag>}
							renderItem={(b) => (
								<Typography.Text ellipsis={{ tooltip: b.name }}>
									{b.name}
								</Typography.Text>
							)}
						/>
					</QueryGuard>
				</BaseContainer>
			</Space>
		</CustomDrawer>
	);
}
